/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HiOutlineCurrencyDollar, HiOutlinePencil, HiOutlineTrash } from "react-icons/hi";

import ListPage from "../../layout/ListPage";
import type { Action, Column } from "../../ui";
import { useCurrencyContext } from "../../context/CurrencyContext";
import { pushOneCurrency } from "../../lib/sysNavigation/SysNavigation";
import { _PAGE_SIZE_ } from "../../lib/other/tools";

type Currency = {
  currency_id: number | string;
  name?: string;
  code?: string;
  symbol?: string;
  [key: string]: unknown;
};

const addRoute = "/currency/add";
const editRoute = "/currency/edit";

const Overview: React.FC = () => {
  const navigate = useNavigate();
  const {
    currency,
    count,
    fetchCurrencies,
    deleteCurrency,
    updateCurrencyState,
  } = useCurrencyContext();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(_PAGE_SIZE_);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!currency) fetchCurrencies();
  }, [currency]);

  const rows = useMemo<Currency[] | null>(() => {
    if (!Array.isArray(currency)) return null;
    const q = query.trim().toLowerCase();
    if (!q) return currency as Currency[];
    return (currency as Currency[]).filter(
      (c) =>
        String(c.name ?? "").toLowerCase().includes(q) ||
        String(c.code ?? "").toLowerCase().includes(q),
    );
  }, [currency, query]);

  const columns: Column<Currency>[] = [
    {
      header: "Currency",
      cell: (row) => (
        <div className="flex items-center gap-3">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand/10 text-brand ring-1 ring-inset ring-brand/15">
            <span className="text-[11px] font-semibold">
              {(row.symbol ?? row.code ?? "$").slice(0, 2)}
            </span>
          </span>
          <div>
            <p className="font-medium text-gray-900">{row.name ?? "—"}</p>
            <p className="text-[11.5px] text-gray-500">{row.code ?? ""}</p>
          </div>
        </div>
      ),
    },
  ];

  const handleDelete = async (row: Currency) => {
    const response: any = await deleteCurrency(row.currency_id);
    if (response?.status === 200 && Array.isArray(currency)) {
      updateCurrencyState(
        (currency as Currency[]).filter((c) => c.currency_id !== row.currency_id),
      );
    }
  };

  const rowActions = (row: Currency): Action[] => [
    {
      label: "Edit",
      icon: <HiOutlinePencil className="h-3.5 w-3.5" />,
      onClick: () => {
        pushOneCurrency(row);
        navigate(editRoute);
      },
    },
    {
      label: "Delete",
      variant: "destructive",
      icon: <HiOutlineTrash className="h-3.5 w-3.5" />,
      onClick: () => handleDelete(row),
      confirm: {
        title: `Delete ${row.name ?? "currency"}?`,
        description:
          "The currency will be removed from every country that references it.",
        confirmLabel: "Delete",
      },
    },
  ];

  return (
    <ListPage<Currency>
      title="Currencies"
      subtitle="Browse, add and manage the currencies the platform uses."
      icon={<HiOutlineCurrencyDollar className="h-5 w-5" />}
      actions={[{ label: "Add currency", variant: "primary", href: addRoute }]}
      rows={rows}
      columns={columns}
      rowKey={(r) => r.currency_id}
      rowActions={rowActions}
      search={{
        value: query,
        onChange: setQuery,
        placeholder: "Search currencies…",
      }}
      pagination={{
        page,
        pageSize,
        total: count,
        onChange: ({ page: p, pageSize: ps }) => {
          setPage(p);
          setPageSize(ps);
          fetchCurrencies();
        },
      }}
      empty="No currencies yet."
    />
  );
};

export default Overview;
